import { useEffect, useState } from 'react';

const UNREAD_THREADS_STORAGE_KEY = 'opal-unread-threads';
const UNREAD_THREADS_CHANGED_EVENT = 'opal::threads::unreadChanged';

/**
 * Hook to track unread threads indicator state from chat-frontend.
 * Listens for custom events and localStorage changes.
 */
export function useUnreadThread() {
  const [hasUnreadThreads, setHasUnreadThreads] = useState(() => {
    const stored = localStorage.getItem(UNREAD_THREADS_STORAGE_KEY);
    if (stored) {
      try {
        const threads = JSON.parse(stored);
        return Array.isArray(threads) && threads.length > 0;
      } catch {
        return false;
      }
    }
    return false;
  });

  useEffect(() => {
    // Listen for custom event from chat-frontend
    const handleUnreadChanged = (event: CustomEvent<{ hasUnreadThreads: boolean }>) => {
      setHasUnreadThreads(event.detail?.hasUnreadThreads ?? false);
    };

    // Listen for storage changes (when updated from another tab or chat-frontend)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === UNREAD_THREADS_STORAGE_KEY) {
        try {
          const threads = event.newValue ? JSON.parse(event.newValue) : [];
          setHasUnreadThreads(Array.isArray(threads) && threads.length > 0);
        } catch {
          setHasUnreadThreads(false);
        }
      }
    };

    document.addEventListener(UNREAD_THREADS_CHANGED_EVENT, handleUnreadChanged as EventListener);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener(
        UNREAD_THREADS_CHANGED_EVENT,
        handleUnreadChanged as EventListener
      );
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return { hasUnreadThreads };
}
